import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search } from "lucide-react";
import { dateLabel, money, useBooks } from "@/lib/books";
import { Input } from "@/components/ui/input";
import { DataTable, ExportMenu, Loaded, PageHeader, Pager, PeriodFilter, StatusText } from "@/components/books/ui";
import { usePeriod } from "@/components/books/Statement";

/** One list per voucher kind — Tally's voucher types, in Zoho's words. */
export const KINDS = {
  invoices: { title: "Invoices", kicker: "Sales", party: "Customer", status: true },
  receipts: { title: "Payments Received", kicker: "Sales", party: "Received from" },
  "credit-notes": { title: "Credit Notes", kicker: "Sales", party: "Customer" },
  bills: { title: "Bills", kicker: "Purchases", party: "Vendor", status: true },
  payments: { title: "Payments Made", kicker: "Purchases", party: "Paid to" },
  "debit-notes": { title: "Vendor Credits", kicker: "Purchases", party: "Vendor" },
  journals: { title: "Journals", kicker: "Accountant", party: "Particulars" },
};

function useDebounced(value, ms = 300) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

export default function Vouchers({ kind }) {
  const copy = KINDS[kind];
  const navigate = useNavigate();
  const [period, setPeriod, fyMonth] = usePeriod("this_fy");
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const query = useDebounced(q);
  useEffect(() => setPage(1), [query, period.from, period.to, kind]);
  const params = { kind, date_from: period.from, date_to: period.to, q: query, page, page_size: 50 };
  const state = useBooks("/books/vouchers", params);

  const columns = [
    { key: "date", label: "Date", render: (v) => dateLabel(v.date), className: "whitespace-nowrap" },
    { key: "number", label: kind === "journals" ? "Journal #" : "Number", className: "whitespace-nowrap", render: (v) => (
      <span className="font-medium text-blue-700">{v.number || "—"}{v.is_cancelled && <span className="ml-2 text-xs font-normal text-rose-600">Cancelled</span>}</span>) },
    kind === "journals"
      ? { key: "voucher_type", label: "Type", render: (v) => v.voucher_type }
      : { key: "party", label: copy.party, render: (v) => v.party || "—" },
    { key: "reference", label: kind === "journals" ? "Narration" : "Reference", render: (v) => (
      <span className="block max-w-xs truncate text-slate-600">{kind === "journals" ? v.narration || "" : v.reference || ""}</span>) },
    ...(copy.status ? [
      { key: "status", label: "Status", render: (v) => <StatusText status={v.status} /> },
      { key: "due_date", label: "Due date", render: (v) => (v.due_date ? dateLabel(v.due_date) : "—"), className: "whitespace-nowrap" },
    ] : []),
    { key: "amount", label: "Amount", align: "right", render: (v) => money(v.amount) },
    ...(copy.status ? [{ key: "balance_due", label: "Balance due", align: "right",
      render: (v) => (v.balance_due === null || v.balance_due === undefined ? "—" : money(v.balance_due)) }] : []),
  ];

  return (
    <div>
      <PageHeader kicker={copy.kicker} title={copy.title}
        subtitle={state.data ? `${state.data.total.toLocaleString("en-IN")} in this period · read from Tally` : null} />
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <PeriodFilter value={period} onChange={setPeriod} fyMonth={fyMonth} />
        <div className="relative">
          <Search size={15} className="absolute left-2.5 top-2.5 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search number, party, reference" className="w-72 pl-8" />
        </div>
        <div className="flex-1" />
        <ExportMenu path="/books/reports/day-book" params={{ kind, date_from: period.from, date_to: period.to }} />
      </div>
      <Loaded state={state}>
        {state.data && (
          <>
            <DataTable columns={columns} rows={state.data.rows} onRowClick={(v) => navigate(`/vouchers/${v.guid}`)}
              empty={`No ${copy.title.toLowerCase()} in this period.`} />
            <Pager page={state.data.page} pageSize={state.data.page_size} total={state.data.total} onChange={setPage} />
          </>
        )}
      </Loaded>
    </div>
  );
}
